/**
 * Viewer Service
 * Handles viewer operations like highlighting, coloring, and camera controls
 *
 * This service provides visualization and interaction capabilities for the Forge Viewer:
 * - Element highlighting and coloring with custom colors
 * - Multi-element selection and isolation
 * - Camera positioning and zoom controls
 * - Bounding box calculations for spatial analysis
 * - Visibility checking for elements in current view
 *
 * All methods are static and stateless for easy access throughout the application.
 * The service maintains a map of original colors to allow restoration of default appearance.
 */

export class ViewerService {
  private static highlightedDbIds = new Set<number>();
  private static originalColors = new Map<number, any>();
  // Cache of ceiling dbIds per model (queried lazily from Revit "Category" property).
  private static ceilingDbIdsCache = new WeakMap<object, number[]>();
  // dbIds we applied a context theming tint to — so we can clear them later
  // without clobbering caller-applied focused status colors.
  private static tintedDbIds = new Set<number>();

  /**
   * Helper function to get Forge Viewer's internal THREE.js instance
   */
  private static getTHREE(viewer: Autodesk.Viewing.GuiViewer3D): any {
    return (window as any).THREE || (viewer as any).impl?.renderer?.THREE;
  }

  /**
   * Enumerate all dbIds in the model. Used to seed category lookups.
   */
  private static enumerateAllDbIds(
    viewer: Autodesk.Viewing.GuiViewer3D
  ): number[] {
    const tree: any = viewer.model?.getInstanceTree();
    if (!tree) return [];
    // NOTE: in this LMV build, enumNodeChildren(id, cb, true) does NOT
    // recurse — it only visits direct children. Walk the tree manually.
    const ids: number[] = [];
    const walk = (id: number) => {
      ids.push(id);
      tree.enumNodeChildren(id, walk, false);
    };
    walk(tree.getRootId());
    return ids;
  }

  /**
   * Returns the cached list of dbIds whose Revit Category is "Revit Ceilings".
   * First call queries getBulkProperties; subsequent calls are O(1).
   */
  static getCeilingDbIds(
    viewer: Autodesk.Viewing.GuiViewer3D
  ): Promise<number[]> {
    const model: any = viewer.model;
    if (!model) return Promise.resolve([]);
    const cached = this.ceilingDbIdsCache.get(model);
    if (cached) return Promise.resolve(cached);

    return new Promise<number[]>((resolve) => {
      const allIds = this.enumerateAllDbIds(viewer);
      if (allIds.length === 0) {
        this.ceilingDbIdsCache.set(model, []);
        resolve([]);
        return;
      }
      const ceilings: number[] = [];
      let remaining = 0;
      const batchSize = 500;
      for (let i = 0; i < allIds.length; i += batchSize) {
        remaining++;
        const batch = allIds.slice(i, i + batchSize);
        // NOTE: Category is a hidden Revit property — `ignoreHidden: false`
        // is required or Category is stripped from the response for all but
        // the Revit Document root.
        model.getBulkProperties(
          batch,
          { propFilter: ["Category"], ignoreHidden: false },
          (results: any[]) => {
            results.forEach((r) => {
              const cat = r.properties?.[0]?.displayValue;
              if (cat === "Revit Ceilings") ceilings.push(r.dbId);
            });
            remaining--;
            if (remaining === 0) {
              this.ceilingDbIdsCache.set(model, ceilings);
              resolve(ceilings);
            }
          },
          () => {
            remaining--;
            if (remaining === 0) {
              this.ceilingDbIdsCache.set(model, ceilings);
              resolve(ceilings);
            }
          }
        );
      }
    });
  }

  /**
   * Apply a near-white theming tint to the given dbIds, visually washing them
   * out so bright status-colored focused elements pop. Used for context/nearby
   * elements. Note: Forge's theming alpha is a tint *weight* (blend strength),
   * NOT true transparency — attempts to use fragment mat.opacity or the
   * dbIdOpacity array did not render reliably in this LMV build. Theming
   * colors do render, so we use them for visual hierarchy instead.
   *
   * Caller is expected to overwrite the tint on focused dbIds with a brighter
   * status color AFTER calling this, so the focused pop against the washed
   * context.
   */
  static applyContextTint(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbIds: number[],
    tintColor?: any
  ): void {
    if (!viewer || dbIds.length === 0) return;
    const THREE = this.getTHREE(viewer);
    if (!THREE) return;
    // Neutral mid-gray at full tint weight — with the slice active, context
    // walls/doors are thin rectangles at cut height; gray reads as "context"
    // without competing with the focused status color (yellow/red/green).
    const color = tintColor || new THREE.Vector4(0.6, 0.6, 0.6, 1.0);
    for (const dbId of dbIds) {
      try { viewer.setThemingColor(dbId, color); } catch { /* noop */ }
      this.tintedDbIds.add(dbId);
    }
    viewer.impl?.invalidate(true, true, true);
  }

  /**
   * Clear every theming tint we applied via applyContextTint. Leaves other
   * theming colors (e.g. caller-applied focused status colors) alone.
   */
  static resetContextTinting(
    viewer: Autodesk.Viewing.GuiViewer3D
  ): void {
    if (!viewer || this.tintedDbIds.size === 0) return;
    this.tintedDbIds.forEach((dbId) => {
      try { viewer.setThemingColor(dbId, null); } catch { /* noop */ }
    });
    this.tintedDbIds.clear();
    viewer.impl?.invalidate(true, true, true);
  }

  /**
   * Kept as a thin wrapper so existing callers that expect these names still
   * compile. The underlying mechanism is theming (not material opacity).
   */
  static applyFragmentOpacity(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbIds: number[],
    _opacity: number
  ): void {
    this.applyContextTint(viewer, dbIds);
  }

  static resetTransparentFragments(
    viewer: Autodesk.Viewing.GuiViewer3D
  ): void {
    this.resetContextTinting(viewer);
  }

  /**
   * Prepare the viewer for a clean isolated render — no Forge ghost wireframes
   * of non-isolated geometry. Mirrors the setup the PDF screenshot pipeline
   * has always used (ComplianceReportPDF.ts). Call this BEFORE viewer.isolate.
   *
   * - Disables the `ghosting` preference (the ghost pass for hidden nodes)
   * - Disables impl.setGhostedMode (belt-and-suspenders)
   * - Resets any fragment material opacities that earlier flows may have
   *   lowered (the Risk Panel sets them to 0.12 in some code paths) back
   *   to 1.0. Fragments tracked by applyFragmentOpacity (ceilings) are
   *   skipped so they stay transparent after re-isolation.
   * - Clears existing theming colors.
   */
  static prepareCleanIsolation(viewer: Autodesk.Viewing.GuiViewer3D): void {
    if (!viewer) return;

    try { viewer.clearThemingColors(); } catch { /* noop */ }
    // Drop any stale cut plane left by a prior slice. Without this, a cut
    // plane set on the previous View click would persist and immediately
    // clip out newly-isolated geometry.
    try { viewer.setCutPlanes([]); } catch { /* noop */ }

    try {
      (viewer as any).prefs?.set("ghosting", false);
    } catch { /* pref key may vary between LMV versions */ }

    try {
      if (viewer.impl && typeof (viewer.impl as any).setGhostedMode === "function") {
        (viewer.impl as any).setGhostedMode(false);
      }
    } catch { /* noop */ }

    // Clear the context tint we applied on the previous isolate. Caller-set
    // focused status colors (applied after the helper returns) aren't in our
    // tracked set, so they survive this too.
    this.resetContextTinting(viewer);

    try {
      const fragList: any = viewer.model?.getFragmentList();
      if (fragList && typeof fragList.getCount === "function") {
        // Reset any stray fragments other flows (e.g. Risk Panel's 0.12
        // global dimming) may have left translucent.
        const count = fragList.getCount();
        for (let fi = 0; fi < count; fi++) {
          const mat = fragList.getMaterial(fi);
          if (mat && mat.opacity < 1.0) {
            mat.opacity = 1.0;
            mat.transparent = false;
            mat.needsUpdate = true;
          }
        }
      }
    } catch { /* best-effort */ }

    viewer.impl?.invalidate(true, true, true);
  }

  /**
   * Isolate the given dbIds while rendering non-focused context elements
   * at partial opacity and Revit Ceilings at near-zero. This keeps the
   * focused elements unobstructed while still showing spatial context.
   *
   * @param focusedIds optional — dbIds that stay fully opaque (the "hero"
   *   elements, e.g. the validation target). Everything in `isolationIds`
   *   that is NOT focused and NOT a ceiling gets `contextOpacity`.
   * @param ceilingOpacity default 0.08 (ceilings: barely visible).
   * @param contextOpacity default 0.3  (nearby walls etc.: see-through).
   */
  static async isolateWithTransparentCeilings(
    viewer: Autodesk.Viewing.GuiViewer3D,
    isolationIds: number[],
    options?: {
      focusedIds?: number[];
      ceilingOpacity?: number;
      contextOpacity?: number;
    }
  ): Promise<number[]> {
    const ceilings = await this.getCeilingDbIds(viewer);
    return this.isolateWithTransparentCeilingsSync(viewer, isolationIds, {
      ...options,
      ceilingIds: ceilings,
    });
  }

  /**
   * Synchronous counterpart of isolateWithTransparentCeilings — requires
   * ceilings to already be cached (call getCeilingDbIds first). Safe to call
   * inside Promise executors / callback chains where `await` isn't available.
   */
  static isolateWithTransparentCeilingsSync(
    viewer: Autodesk.Viewing.GuiViewer3D,
    isolationIds: number[],
    options?: {
      focusedIds?: number[];
      ceilingOpacity?: number;  // kept for API compat; ignored (ceilings hidden)
      contextOpacity?: number;  // kept for API compat; ignored (theming used)
      ceilingIds?: number[];
    }
  ): number[] {
    // Ceilings: simply NOT adding them to the isolation list means isolate()
    // hides them. No extra hide() call needed — that would incorrectly also
    // hide any ceiling that's in the focused/isolation set. Forge's
    // per-fragment opacity doesn't render reliably in this LMV build, so we
    // accept "ceilings hidden" as the tradeoff.
    viewer.isolate(isolationIds);

    // Context: white-tint any isolated element that isn't focused so the
    // focused elements pop when the caller applies their status color on top.
    if (options?.focusedIds && options.focusedIds.length > 0) {
      const focused = new Set(options.focusedIds);
      const contextIds = isolationIds.filter((id) => !focused.has(id));
      if (contextIds.length > 0) {
        this.applyContextTint(viewer, contextIds);
      }
    }

    // Caller passes ceilingIds in options only for API compat — we ignore
    // them in the isolation list to keep ceilings hidden.
    void options?.ceilingIds;
    return isolationIds;
  }

  /**
   * Compute the floor Z for a focus set — 20th-percentile of each focused
   * element's min-Z. Robust against a ceiling from the floor below sneaking
   * into the focus set.
   */
  private static inferFloorZ(
    viewer: Autodesk.Viewing.GuiViewer3D,
    focusedIds: number[]
  ): number | null {
    const THREE = this.getTHREE(viewer);
    if (!THREE) return null;
    const model: any = viewer.model;
    const tree: any = model?.getInstanceTree?.();
    const fragList: any = model?.getFragmentList?.();
    if (!tree || !fragList || !focusedIds?.length) return null;
    const tmp = new THREE.Box3();
    const minZs: number[] = [];
    for (const id of focusedIds) {
      const perBox = new THREE.Box3();
      perBox.makeEmpty();
      tree.enumNodeFragments(
        id,
        (fi: number) => {
          fragList.getWorldBounds(fi, tmp);
          perBox.union(tmp);
        },
        false
      );
      if (isFinite(perBox.min.z)) minZs.push(perBox.min.z);
    }
    if (minZs.length === 0) return null;
    minZs.sort((a, b) => a - b);
    return minZs[Math.floor(minZs.length * 0.2)];
  }

  /**
   * Return the subset of `dbIds` whose bounding box intersects the vertical
   * band `[floorZ, floorZ + feetAboveFloor]` — i.e. elements that physically
   * exist anywhere in the band.
   *
   * Walls that run from slab to ceiling intersect the band and are kept
   * (rendered in full — no clipping). Ceilings and objects mounted to them
   * sit above the band and are filtered out.
   *
   * `focusedIds` are always kept regardless of vertical position so the
   * target element never disappears from its own view.
   */
  static filterByFloorBand(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbIds: number[],
    focusedIds: number[],
    feetAboveFloor: number = 2
  ): number[] {
    if (!viewer || !dbIds?.length) return dbIds || [];
    const THREE = this.getTHREE(viewer);
    const model: any = viewer.model;
    const tree: any = model?.getInstanceTree?.();
    const fragList: any = model?.getFragmentList?.();
    if (!THREE || !tree || !fragList) return dbIds;

    const floorZ = this.inferFloorZ(viewer, focusedIds);
    if (floorZ == null) return dbIds;

    const unit = model.getUnitString?.() || "ft";
    const factor = unit === "ft" ? 1 : unit === "in" ? 12 : unit === "m" ? 0.3048 : unit === "mm" ? 304.8 : 1;
    const topZ = floorZ + feetAboveFloor * factor;

    const keep = new Set<number>(focusedIds || []);
    const tmp = new THREE.Box3();
    for (const id of dbIds) {
      if (keep.has(id)) continue;
      const box = new THREE.Box3();
      box.makeEmpty();
      tree.enumNodeFragments(
        id,
        (fi: number) => {
          fragList.getWorldBounds(fi, tmp);
          box.union(tmp);
        },
        false
      );
      if (!isFinite(box.min.z)) continue;
      // Intersects band if bbox isn't entirely above topZ and isn't entirely below floorZ.
      if (box.min.z <= topZ && box.max.z >= floorZ) keep.add(id);
    }
    return Array.from(keep);
  }

  /**
   * Highlight elements with custom color
   */
  static highlightElements(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbIds: number[],
    color?: THREE.Vector4,
    isolate: boolean = true
  ) {
    if (!viewer) return;

    // Clear previous highlights
    this.clearHighlights(viewer);

    // Get THREE.js instance first
    const THREE = this.getTHREE(viewer);
    if (!THREE) {
      console.error("[ViewerService] THREE.js not available");
      return;
    }

    // Default to blue if no color specified - ensure THREE is available before using it
    const highlightColor = color || new THREE.Vector4(0, 0.5, 1, 0.5); // Blue with transparency

    // Isolate elements if requested
    if (isolate) {
      viewer.isolate(dbIds);
    }

    // Apply color to elements
    dbIds.forEach((dbId) => {
      viewer.setThemingColor(dbId, highlightColor);
      this.highlightedDbIds.add(dbId);
    });

    // Fit camera to highlighted elements
    viewer.fitToView(dbIds);
  }

  /**
   * Highlight a single element
   */
  static highlightElement(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number,
    color?: THREE.Vector4,
    isolate: boolean = true
  ) {
    this.highlightElements(viewer, [dbId], color, isolate);
  }

  /**
   * Set color for surrounding elements (for future use)
   */
  static colorSurroundingElements(
    viewer: Autodesk.Viewing.GuiViewer3D,
    centerDbId: number,
    surroundingDbIds: number[],
    centerColor?: THREE.Vector4,
    surroundingColor?: THREE.Vector4
  ) {
    if (!viewer) return;

    // Clear previous highlights
    this.clearHighlights(viewer);

    // Get THREE.js instance first
    const THREE = this.getTHREE(viewer);
    if (!THREE) {
      console.error("[ViewerService] THREE.js not available");
      return;
    }

    // Colors - ensure THREE is available before using it
    const blue = centerColor || new THREE.Vector4(0, 0.5, 1, 0.6); // Blue for center
    const gray = surroundingColor || new THREE.Vector4(0.5, 0.5, 0.5, 0.3); // Gray for surrounding

    // Isolate all elements
    const allDbIds = [centerDbId, ...surroundingDbIds];
    viewer.isolate(allDbIds);

    // Color center element
    viewer.setThemingColor(centerDbId, blue);
    this.highlightedDbIds.add(centerDbId);

    // Color surrounding elements
    surroundingDbIds.forEach((dbId) => {
      viewer.setThemingColor(dbId, gray);
      this.highlightedDbIds.add(dbId);
    });

    // Focus on center element
    viewer.fitToView([centerDbId]);
  }

  /**
   * Clear all highlights and restore original view
   */
  static clearHighlights(viewer: Autodesk.Viewing.GuiViewer3D) {
    if (!viewer) return;

    // Clear theming colors
    this.highlightedDbIds.forEach((dbId) => {
      viewer.setThemingColor(dbId, null);
    });
    this.highlightedDbIds.clear();

    // Show all elements
    viewer.isolate([]);
    viewer.showAll();
  }

  /**
   * Zoom to specific elements without changing colors
   */
  static zoomToElements(viewer: Autodesk.Viewing.GuiViewer3D, dbIds: number[]) {
    if (!viewer) return;
    viewer.fitToView(dbIds);
  }

  /**
   * Get bounding box for elements
   */
  static getElementsBoundingBox(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbIds: number[]
  ): THREE.Box3 | null {
    if (!viewer || !viewer.model) return null;

    const fragList = viewer.model.getFragmentList();
    const THREE = this.getTHREE(viewer);
    if (!THREE) {
      console.error("[ViewerService] THREE.js not available");
      return null;
    }

    const box = new THREE.Box3();
    let initialized = false;

    dbIds.forEach((dbId) => {
      const instanceTree = viewer.model.getInstanceTree();
      if (instanceTree) {
        instanceTree.enumNodeFragments(
          dbId,
          (fragId: number) => {
            const fragBox = new THREE.Box3();
            fragList.getWorldBounds(fragId, fragBox);

            if (!initialized) {
              box.copy(fragBox);
              initialized = true;
            } else {
              box.union(fragBox);
            }
          },
          true
        );
      }
    });

    return initialized ? box : null;
  }

  /**
   * Expand a set of involved dbIds to include spatially nearby elements,
   * via a padded bounding-box intersection scan. Mirrors the algorithm used
   * by the PDF screenshot pipeline. Returns involved ∪ nearby (deduped, with
   * involved ids first). Falls back to the input list on any failure.
   */
  static findNearbyElements(
    viewer: Autodesk.Viewing.GuiViewer3D,
    involvedDbIds: number[],
    options?: {
      horizontalPadRatio?: number;
      minPad?: number;
      maxPad?: number;
      padBelow?: number;
      padAbove?: number;
    }
  ): number[] {
    const involved = involvedDbIds.filter((id) => Number.isFinite(id) && id > 0);
    if (!viewer || !viewer.model || involved.length === 0) return involved;

    try {
      const model = viewer.model;
      const instanceTree = model.getInstanceTree();
      const fragList = model.getFragmentList();
      const THREE = this.getTHREE(viewer);
      if (!instanceTree || !fragList || !THREE) return involved;

      const horizontalPadRatio = options?.horizontalPadRatio ?? 0.8;
      const minPad = options?.minPad ?? 3;
      const maxPad = options?.maxPad ?? 10;
      const padBelow = options?.padBelow ?? 0.5;
      const padAbove = options?.padAbove ?? 0.3;

      const involvedBox = new THREE.Box3();
      for (const dbId of involved) {
        instanceTree.enumNodeFragments(
          dbId,
          (fragId: number) => {
            const fb = new THREE.Box3();
            fragList.getWorldBounds(fragId, fb);
            involvedBox.union(fb);
          },
          true
        );
      }

      if (involvedBox.isEmpty()) return involved;

      const invSize = involvedBox.getSize(new THREE.Vector3());
      const hPad = Math.min(
        Math.max(Math.max(invSize.x, invSize.y) * horizontalPadRatio, minPad),
        maxPad
      );
      const searchBox = involvedBox.clone();
      searchBox.min.x -= hPad;
      searchBox.min.y -= hPad;
      searchBox.min.z -= padBelow;
      searchBox.max.x += hPad;
      searchBox.max.y += hPad;
      searchBox.max.z += padAbove;

      const result: number[] = [...involved];
      const involvedSet = new Set(involved);
      const fragBox = new THREE.Box3();

      instanceTree.enumNodeChildren(
        instanceTree.getRootId(),
        (childId: number) => {
          if (involvedSet.has(childId)) return;
          let hits = false;
          instanceTree.enumNodeFragments(
            childId,
            (fragId: number) => {
              if (hits) return;
              fragList.getWorldBounds(fragId, fragBox);
              if (searchBox.intersectsBox(fragBox)) {
                hits = true;
              }
            },
            false
          );
          if (hits) result.push(childId);
        },
        true
      );

      return result;
    } catch (err) {
      console.debug("[ViewerService] findNearbyElements failed:", err);
      return involved;
    }
  }

  /**
   * Check if elements are visible in current view
   */
  static areElementsVisible(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbIds: number[]
  ): boolean {
    if (!viewer) return false;

    const camera = viewer.getCamera();
    const THREE = this.getTHREE(viewer);
    if (!THREE) {
      console.error("[ViewerService] THREE.js not available");
      return false;
    }

    const frustum = new THREE.Frustum();
    const matrix = new THREE.Matrix4().multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse
    );
    frustum.setFromProjectionMatrix(matrix);

    const box = this.getElementsBoundingBox(viewer, dbIds);
    if (!box) return false;

    return frustum.intersectsBox(box);
  }
}
